// src/pages/Home.tsx
import { useCallback } from 'react';
import Layout from '@/components/Layout';
// SEO: Import SEO component for authenticated home dashboard overview.
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Sparkles, Rocket, Info, Brain, Zap, Layers3, ShieldCheck, ArrowBigRight, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'wouter';
import { useActiveAnalyses } from '@/hooks/useAnalysis';

// NOTE: News section removed per request

export default function Home() {
  const [, setLocation] = useLocation();
  const { data: activeAnalyses } = useActiveAnalyses();
  
  const goTrading = useCallback(() => { setLocation('/start-trading'); }, [setLocation]);
  const livePreviewActive: any[] = Array.isArray(activeAnalyses)
    ? activeAnalyses
        .filter((a: any) => String(a?.status || '').toUpperCase() !== 'COMPLETED')
        .slice(0, 3)
    : [];

  return (
    <Layout>
      {/* SEO TAGS: Authenticated user landing (home). Indexed but same canonical as root retained for logged-in variant; noindex avoided to preserve personalized engagement signals. */}
      <SEO
        title="Member Home | TradeSagePro AI Market Research"
        description="Access AI market research insights, live analyses, and a token-balanced usage dashboard at TradeSagePro."
        canonical="https://tradesagepro.com/" /* Root canonical to avoid duplicate /home path issues */
        type="profile"
      />
      {/* END SEO TAGS */}
      <div className="relative min-h-screen bg-[#060A15] text-[#E8F3FB] overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          {/* Reduce heavy blur on small screens to avoid repaint/flash during fast mobile scrolls */}
          <div
            className="absolute -top-40 -left-44 w-[48rem] h-[48rem] bg-gradient-to-br from-[#00D1FF]/30 via-[#1D4ED8]/10 to-transparent md:blur-3xl blur-none animate-pulse-slow"
            style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
          />
          <div
            className="absolute top-1/3 -right-52 w-[50rem] h-[50rem] bg-gradient-to-tr from-[#7C3AED]/25 via-[#1E3A8A]/25 to-transparent md:blur-3xl blur-none animate-pulse-slower"
            style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
          />
          <div className="absolute inset-0 opacity-[0.4] bg-[radial-gradient(circle_at_70%_30%,rgba(124,58,237,0.20),transparent_65%)]" />
          <div className="absolute inset-x-0 top-0 h-[480px] overflow-hidden">
            <div
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-[1400px] h-[700px] bg-[conic-gradient(from_180deg_at_50%_50%,#5B21B6,#0369A1,#5B21B6)] opacity-[0.18] animate-spin-slow rounded-full md:blur-[110px] blur-none"
              style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
            />
          </div>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10 max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-28"
        >
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F2336]/80 border border-[#1E3E56] text-[11px] tracking-wider uppercase text-[#6DDCFF] backdrop-blur">
                <Sparkles className="w-3 h-3" />
                AI Crypto Market Research Platform
              </div>
              <h1 className="mt-6 text-4xl md:text-5xl xl:text-[3.6rem] font-extrabold leading-tight bg-gradient-to-br from-white via-[#DFF8FF] to-[#3BC8F9] bg-clip-text text-transparent">
                Structured Data-Driven Analysis Insights for Crypto
              </h1>
              <p className="mt-6 text-lg md:text-xl text-[#B7CAD7] leading-relaxed max-w-xl">
                Structured, data-driven analysis of market structure, volatility, and momentum — surfaced when conditions align.
              </p>
              <p className="mt-4 text_sm text-[#8CA3B2] max-w-xl">
                Tokens are deducted when a validated analysis output is produced. If the outcome is Neutral/No Output, your reserved token may be returned per policy.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={goTrading}
                  className="relative group px-9 py-5 rounded-2xl text-base font-semibold bg-gradient-to-r from-[#00D1FF] via-[#0EA5E9] to-[#10B981] hover:from-[#36E5FF] hover:via-[#30C4F4] hover:to-[#34D399] transition-all shadow-[0_0_0_0_rgba(0,209,255,0.35)] hover:shadow-[0_0_28px_-4px_rgba(0,209,255,0.55)]"
                >
                  <span className="flex items-center relative z-10">
                    <Rocket className="w-5 h-5 mr-2" />
                    Start Analysis
                  </span>
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_28%_30%,rgba(255,255,255,0.35),transparent_62%)] transition-opacity" />
                </Button>
                <Link href="/dashboard">
                  <Button
                    variant="outline"
                    className="px-9 py-5 rounded-2xl text-base font-semibold border border-[#1D4762] bg-[#0B1C2B]/60 hover:bg-[#123347] hover:border-[#00D1FF] hover:text-[#00D1FF] transition-all"
                  >
                    <Info className="w-5 h-5 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
              {/* Removed counters per request: signals generated / active */}
            </div>

            <div className="relative">
              <div
                className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_50%,rgba(0,209,255,0.25),transparent_65%)] md:blur-2xl blur-none opacity-70"
                style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
              />
              <div className="relative rounded-3xl border border-[#1E3F55] bg-gradient-to-br from-[#081522] via-[#0C1F30] to-[#07111C] overflow-hidden shadow-2xl p-8">
                <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_75%_25%,rgba(124,58,237,0.35),transparent_60%)]" />
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="relative">
                    <div
                      className="w-40 h-40 rounded-full bg-gradient-to-br from-[#00D1FF] via-[#4F46E5] to-[#7C3AED] opacity-20 md:blur-2xl blur-none"
                      style={{ willChange: "transform, opacity", transform: "translateZ(0)" }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Brain className="w-24 h-24 text-[#38D0F7]" />
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-[#9EB4C4] max-w-sm">
                    Multi-factor observational logic monitors liquidity zones,
                    structural pivots and momentum shifts before indicating a
                    directional bias.
                  </p>
                  <div className="flex gap-4">
                    {["Structure", "Momentum", "Volatility"].map((k, i) => (
                      <span
                        key={k}
                        className="text-[11px] px-3 py-1 rounded-full bg-[#0F2B3F] border border-[#1A4E6A] text-[#6EDBFF] tracking-wide"
                        style={{ animation: `fadeIn .5s ease ${i * 0.12}s both` }}
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
  </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10 max-w-7xl mx-auto px-6 pb-24"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-10 bg-gradient-to-r from-white via-[#DFF7FF] to-[#7ADFFF] bg-clip-text text-transparent">
            Why TradeSagePro?
          </h2>
          <div className="grid gap-7 md:grid-cols-3">
            {[
              {
                icon: <Zap className="w-7 h-7" />,
                title: "Real-Time Adaptation",
                desc: "Analysis reacts to live structure & momentum.",
                gradient:
                  "from-[#052c46] via-[#0b4b71] to-[#0d2f4c] shadow-cyan-500/30",
              },
              {
                icon: <Layers3 className="w-7 h-7" />,
                title: "AI Confirmation",
                desc: "No rushing—waits for aligned conditions.",
                gradient:
                  "from-[#251d55] via-[#3a2f7d] to-[#221942] shadow-violet-500/30",
              },
              {
                icon: <ShieldCheck className="w-7 h-7" />,
                title: "Fair Tokens",
                desc: "Tokens apply to validated analysis outputs. Neutral/No Output may return the reserved token.",
                gradient:
                  "from-[#06352f] via-[#0c5249] to-[#08332d] shadow-emerald-500/30",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="group relative rounded-2xl p-[2px] overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.01))",
                }}
              >
                <div
                  className={`rounded-2xl h-full w-full bg-gradient-to-br ${b.gradient} p-6 relative overflow-hidden transition-all duration-300 group-hover:scale-[1.015]`}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.18),transparent_60%)]" />
                  <div className="w-12 h-12 mb-5 rounded-xl flex items-center justify-center bg-gradient-to-br from-white/90 to-white/60 text-[#0D2332] shadow">
                    {b.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    {b.title}
                  </h3>
                  <p className="text-sm text-[#C9D7E1] mt-2 leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
  </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10 max-w-7xl mx-auto px-6 pb-28"
        >
            <h2 className="text-2xl md:text-3xl font-bold mb-12 bg-gradient-to-r from-white via-[#C9ECFF] to-[#5BD9FF] bg-clip-text text-transparent">
              How It Works
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#1B5A81] to-transparent" />
              <div className="grid gap-10 md:grid-cols-3">
                {[
                  {
                    step: "1",
                    title: "Select Pair",
                    text: "Choose the crypto market you want analyzed.",
                    color: "from-[#00D1FF] to-[#0EA5E9]",
                  },
                  {
                    step: "2",
                    title: "Run Analysis",
                    text: "AI observes structure, momentum & volatility first.",
                    color: "from-[#6366F1] to-[#8B5CF6]",
                  },
                  {
                    step: "3",
                    title: "Get Outcome",
                    text: "Validated analysis outputs consume tokens. Neutral/No Output may return it.",
                    color: "from-[#10B981] to-[#34D399]",
                  },
                ].map((s, i) => (
                  <div
                    key={s.step}
                    className="relative flex flex-col items-start md:items-center text-left md:text-center"
                  >
                    <div
                      className={`relative z-10 w-16 h-16 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center font-bold text-xl text-[#041F2B] shadow-lg`}
                    >
                      {s.step}
                    </div>
                    {i < 2 && (
                      <ArrowBigRight className="hidden md:block absolute top-1/2 -right-6 w-8 h-8 text-[#1E597B] -translate-y-1/2" />
                    )}
                    <h3 className="mt-6 text-lg font-semibold text-white">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#8FAFC2] max-w-xs leading-relaxed">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-10 text-[12px] md:text-xs text-[#6D8899]">
                Fair Use: Tokens apply to validated analysis outputs. Neutral/No Output may return the reserved token automatically per policy.
              </p>
            </div>
  </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.12 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10 max-w-7xl mx-auto px-6 pb-24"
        >
          <div className="flex items-center justify-between mb-7">
            <h2 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-white via-[#E6FAFF] to-[#54D7FF] bg-clip-text text-transparent">
              Live Preview
            </h2>
            <Link href="/dashboard">
              <Button
                variant="outline"
                className="group border-[#1E4963] bg-[#0C1F30]/60 hover:border-[#00D1FF] hover:text-[#00D1FF] hover:bg-[#12354B] transition-colors h-10 px-5 text-sm"
              >
                See all analyses
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
          {livePreviewActive.length ? (
            <div className="flex flex-col md:flex-row gap-6">
              {livePreviewActive.map((a, idx) => {
                const raw = Number((a as any).progress_percent ?? 0);
                // treat 0..1 as indeterminate early phase
                const isIndeterminate = !raw || raw < 1;
                const shownPercent = Math.round(Math.min(Math.max(raw, 0), 100));
                return (
                  <div
                    key={(a as any).id || (a as any).request_id || (a as any).analysis_request_id || idx}
                    className="flex-1 group relative rounded-xl border border-[#1D4760] bg-gradient-to-br from-[#102232] via-[#0C1B29] to-[#09131F] p-5 flex flex-col gap-4 hover:border-[#2E7DA1] transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-white">
                        {String((a as any).symbol || 'PAIR').toUpperCase()}
                      </p>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30 font-semibold tracking-wide">
                        LIVE
                      </span>
                    </div>
                    <div className="text-[11px] text-[#7DA3B8] flex items-center gap-4">
                      <span>
                        {new Date((a as any).created_at || Date.now()).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <span className="text-[#C9D7E2]">
                        {String((a as any).status || 'IN_PROGRESS').toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full rounded-full bg-[#122838] overflow-hidden relative ring-1 ring-[#1F4255]/50">
                        {/* subtle inner gradient base */}
                        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.05)_0%,transparent_40%,transparent_60%,rgba(255,255,255,0.05)_100%)] opacity-40" />
                        {isIndeterminate ? (
                          <>
                            <motion.div
                              className="absolute left-0 top-0 h-full w-1/3 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-emerald-400 shadow-[0_0_8px_-2px_rgba(56,189,248,0.6)]"
                              animate={{ x: ['-30%', '130%'] }}
                              transition={{ duration: 2.2, repeat: Infinity, ease: [0.55,0.15,0.25,0.9] }}
                            />
                            <motion.div
                              className="absolute left-0 top-0 h-full w-1/4 rounded-full bg-gradient-to-r from-emerald-300/40 via-transparent to-transparent"
                              animate={{ x: ['-10%', '160%'] }}
                              transition={{ duration: 3.4, repeat: Infinity, ease: 'linear' }}
                            />
                          </>
                        ) : (
                          <motion.div
                            className="h-full rounded-full relative overflow-hidden"
                            initial={false}
                            animate={{ width: `${shownPercent}%` }}
                            transition={{ type: 'spring', stiffness: 120, damping: 24, mass: 0.6 }}
                            style={{ background: 'linear-gradient(90deg,#06b6d4,#0ea5e9,#10b981)' }}
                          >
                            {/* shimmer */}
                            <motion.div
                              className="absolute inset-0 opacity-40 bg-[linear-gradient(110deg,transparent,rgba(255,255,255,0.35),transparent)]"
                              animate={{ x: ['-15%', '130%'] }}
                              transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
                            />
                          </motion.div>
                        )}
                        {/* soft glow edge */}
                        {!isIndeterminate && shownPercent > 0 && (
                          <div
                            className="absolute top-0 bottom-0 -right-1 w-6 pointer-events-none"
                            style={{ background: 'radial-gradient(circle_at_left,rgba(56,189,248,0.55),rgba(56,189,248,0)_70%)' }}
                          />
                        )}
                      </div>
                      <div className="flex justify-between text-[10px] text-[#6FA2BA] font-medium tracking-wide">
                        <span className="uppercase">Progress</span>
                        {!isIndeterminate && <span className="tabular-nums text-cyan-300">{shownPercent}%</span>}
                      </div>
                    </div>
                    <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity bg-[linear-gradient(120deg,rgba(0,209,255,0.08),transparent_60%)] rounded-xl" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-[#1B455D] bg-[#0D1F2C]/60 px-6 py-12 text-center">
              <p className="text-sm text-white font-medium mb-2">No live analyses</p>
              <p className="text-xs text-[#86A1B2] mb-5">Start one to see it here in real time.</p>
              <Button
                onClick={goTrading}
                className="h-10 px-6 bg-gradient-to-r from-[#00D1FF] to-[#0EA5E9] hover:from-[#36DEFF] hover:to-[#3ABFF0] text-[#062438] text-sm font-semibold"
              >
                Run Analysis
              </Button>
            </div>
          )}
        </motion.section>
        
        {/* Entire news section removed per request */}
      </div>
    </Layout>
  );
}